import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from "@angular/core";
import { Observable } from "rxjs";
import { ToolsService } from "../../../services/tools.service";
import { WindowConfig, WindowManagerService } from "../window-manager.service";

// Ajout d'un type pour la direction de redimensionnement
export type ResizeDirection = "n" | "s" | "e" | "w" | "se";

@Component({
  selector: "app-window-host",
  templateUrl: "./window-host.component.html",
  styleUrls: ["./window-host.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WindowHostComponent {
  windows$: Observable<WindowConfig[]> = this.windowService.windows$;
  snapPreview: "left" | "right" | "top" | null = null;
  activeWindow: WindowConfig | null = null;
  isResizing = false;
  resizeWin: WindowConfig | null = null;
  resizeDirection: ResizeDirection | null = null;
  isMobile: boolean;
  draggingWindowId: number | null = null;

  constructor(
    public windowService: WindowManagerService,
    private tools: ToolsService,
    private cdr: ChangeDetectorRef
  ) {
    this.isMobile = this.tools.isMobileSupport();
  }

  // Fonction trackBy pour optimiser le rendu
  trackByWindow(index: number, win: WindowConfig): string {
    return `${win.type}-${win.id}`;
  }

  setActiveWindow(win: WindowConfig) {
    this.windowService.select(win); // met isActive: true sur win, false sur les autres
    this.activeWindow = win;
    this.cdr.markForCheck();
  }

  getStyle(win: WindowConfig) {
    if (!win.maximized && !win.anchored && !win.minimized) {
      return {
        left: (win.position?.x ?? window.innerWidth / 6) + "px",
        top: (win.position?.y ?? window.innerHeight / 6) + "px",
        width: "600px",
        height: "400px",
      };
    }
    return {};
  }

  getWindowStyle(win: WindowConfig) {
    // Pour les fenêtres ancrées ou maximisées, on force les dimensions
    if (win.anchored === "left") {
      return {
        left: "0px",
        top: "0px",
        width: "50%",
        height: "100%",
        position: "absolute",
      };
    }

    if (win.anchored === "right") {
      return {
        right: "0px",
        top: "0px",
        width: "50%",
        height: "100%",
        position: "absolute",
      };
    }

    if (win.maximized) {
      return {
        left: "0px",
        top: "0px",
        width: "100%",
        height: "100%",
        position: "absolute",
      };
    }

    // Pour les fenêtres normales
    const base = this.getStyle(win);
    return {
      ...base,
      width: win.width ? win.width + "px" : base.width,
      height: win.height ? win.height + "px" : base.height,
    };
  }

  onDragStarted(win: WindowConfig) {
    if (win.anchored || win.maximized) {
      this.windowService.update(win, {
        anchored: null,
        maximized: false,
        // On garde la position actuelle pour que le drag parte du bon endroit
        position: undefined,
      });
    }
    this.draggingWindowId = win.id;
    this.cdr.markForCheck();
  }

  onDragMoved(event: any) {
    const margin = 32;
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const x = event.pointerPosition.x;
    const y = event.pointerPosition.y;
    if (x <= margin) {
      this.snapPreview = "left";
    } else if (x >= screenW - margin) {
      this.snapPreview = "right";
    } else if (y <= margin) {
      this.snapPreview = "top";
    } else {
      this.snapPreview = null;
    }
    this.cdr.markForCheck();
  }

  onDragEnded(event: any, win: WindowConfig) {
    const prevX = win.position?.x ?? window.innerWidth / 6;
    const prevY = win.position?.y ?? window.innerHeight / 6;
    const { x: dx, y: dy } = event.source.getFreeDragPosition();
    const newX = prevX + dx;
    const newY = prevY + dy;

    if (this.snapPreview === "left") {
      this.windowService.update(win, {
        anchored: "left",
        maximized: false,
        minimized: false,
        position: undefined,
      });
    } else if (this.snapPreview === "right") {
      this.windowService.update(win, {
        anchored: "right",
        maximized: false,
        minimized: false,
        position: undefined,
      });
    } else if (this.snapPreview === "top") {
      this.windowService.update(win, {
        maximized: true,
        anchored: null,
        minimized: false,
        position: undefined,
      });
    } else {
      this.windowService.update(win, {
        position: { x: newX, y: newY },
        anchored: null,
        maximized: false,
        minimized: false,
      });
    }
    event.source._dragRef.reset();
    this.snapPreview = null;
    this.draggingWindowId = null;
    this.cdr.markForCheck();
  }

  // Nouvelle méthode de redimensionnement
  startResize(
    event: MouseEvent,
    win: WindowConfig,
    direction: ResizeDirection = "se"
  ) {
    event.stopPropagation();
    event.preventDefault();
    this.isResizing = true;
    this.resizeWin = win;
    this.resizeDirection = direction;
    document.addEventListener("mousemove", this.onResizing);
    document.addEventListener("mouseup", this.stopResize);
  }

  onResizing = (event: MouseEvent) => {
    if (!this.isResizing || !this.resizeWin || !this.resizeDirection) return;
    const minWidth = 200,
      minHeight = 100;
    let { x: x0, y: y0 } = this.resizeWin.position ?? {
      x: window.innerWidth / 6,
      y: window.innerHeight / 6,
    };
    let width = this.resizeWin.width ?? 600;
    let height = this.resizeWin.height ?? 400;
    let newX = x0;
    let newY = y0;

    switch (this.resizeDirection) {
      case "se":
        width = Math.max(minWidth, event.clientX - x0);
        height = Math.max(minHeight, event.clientY - y0);
        break;
      case "e":
        width = Math.max(minWidth, event.clientX - x0);
        break;
      case "s":
        height = Math.max(minHeight, event.clientY - y0);
        break;
      case "n":
        height = Math.max(minHeight, height + (y0 - event.clientY));
        newY = Math.min(event.clientY, y0 + height - minHeight);
        break;
      case "w":
        width = Math.max(minWidth, width + (x0 - event.clientX));
        newX = Math.min(event.clientX, x0 + width - minWidth);
        break;
    }
    this.windowService.update(this.resizeWin, {
      width,
      height,
      position: { x: newX, y: newY },
    });
  };

  stopResize = () => {
    this.isResizing = false;
    this.resizeWin = null;
    this.resizeDirection = null;
    document.removeEventListener("mousemove", this.onResizing);
    document.removeEventListener("mouseup", this.stopResize);
  };

  minimize(win: WindowConfig) {
    this.windowService.minimize(win);
  }
  maximize(win: WindowConfig) {
    this.windowService.maximize(win);
  }
  restore(win: WindowConfig) {
    this.windowService.restore(win);
  }
  anchor(win: WindowConfig, side: "left" | "right") {
    this.windowService.anchor(win, side);
  }
  unanchor(win: WindowConfig) {
    this.windowService.update(win, { anchored: null, maximized: false });
  }
  close(win: WindowConfig) {
    // Sur mobile, s'assurer que la fermeture fonctionne correctement
    if (this.isMobile) {
      // Forcer la détection de changement pour s'assurer que la fermeture est bien traitée
      this.cdr.markForCheck();
      
      // Réinitialiser l'état tactile pour éviter les conflits
      this.resetTouchState();
    }
    this.windowService.close(win);
  }
  bringToFront(win: WindowConfig) {
    this.windowService.select(win);
  }

  // Focus automatique sur la fenêtre active
  onFocus(win: WindowConfig) {
    this.setActiveWindow(win);
  }

  // Navigation clavier dans la fenêtre
  onKeyDown(event: KeyboardEvent, win: WindowConfig) {
    if (event.key === "Escape") {
      this.close(win);
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // Tabulation cyclique dans la fenêtre
    if (event.key === "Tab") {
      const container = (event.target as HTMLElement).closest(
        ".window-container"
      );
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden")
      );
      if (focusables.length === 0) return;
      const currentIndex = focusables.indexOf(
        document.activeElement as HTMLElement
      );
      let nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0) nextIndex = focusables.length - 1;
      if (nextIndex >= focusables.length) nextIndex = 0;
      focusables[nextIndex].focus();
      event.preventDefault();
    }
  }

  // Gestion du clavier sur la barre de titre (double-clic, etc.)
  onTitleBarKeyDown(event: KeyboardEvent, win: WindowConfig) {
    if (event.key === "Enter" || event.key === " ") {
      this.bringToFront(win);
      event.preventDefault();
    }
    if (event.key === "Enter" && event.ctrlKey) {
      // Double-clic clavier = maximiser/restaurer
      if (win.maximized) {
        this.restore(win);
      } else {
        this.maximize(win);
      }
      event.preventDefault();
    }
  }

  // Gestion du swipe mobile pour minimiser/fermer
  private touchStartY: number | null = null;
  private touchDeltaY: number = 0;
  private touchStartTime: number | null = null;
  private touchStartX: number | null = null;
  private touchDeltaX: number = 0;
  private isClickingButton: boolean = false;

  onTouchStart(event: TouchEvent, win: WindowConfig) {
    if (!this.isMobile) {
      return;
    }
    
    // Vérifier si on clique sur un bouton ou dans la zone des contrôles
    const target = event.target as HTMLElement;
    const isButtonClick = target.closest('button') || target.closest('.window-controls');
    
    if (isButtonClick) {
      this.isClickingButton = true;
      // Ne pas empêcher le comportement par défaut pour les boutons
      return;
    }
    
    this.isClickingButton = false;
    
    // Seulement empêcher le scroll si on n'est pas sur un bouton
    event.preventDefault();
    
    this.touchStartY = event.touches[0].clientY;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartTime = Date.now();
    this.touchDeltaY = 0;
    this.touchDeltaX = 0;
  }

  onTouchMove(event: TouchEvent, win: WindowConfig) {
    if (!this.isMobile || this.touchStartY === null || this.isClickingButton) return;
    event.preventDefault(); // Empêcher le scroll pendant le swipe
    const currentY = event.touches[0].clientY;
    const currentX = event.touches[0].clientX;
    this.touchDeltaY = currentY - this.touchStartY;
    this.touchDeltaX = currentX - (this.touchStartX || 0);
  }

  onTouchEnd(event: TouchEvent, win: WindowConfig) {
    if (!this.isMobile) return;

    // Si on était en train de cliquer sur un bouton, ne pas traiter le swipe
    if (this.isClickingButton) {
      this.resetTouchState();
      return;
    }

    if (this.touchStartY === null || this.touchStartTime === null) {
      this.resetTouchState();
      return;
    }

    const touchDuration = Date.now() - this.touchStartTime;

    // Swipe vers le bas détecté (plus de 50px en moins de 500ms) avec mouvement principalement vertical
    if (this.touchDeltaY > 50 && touchDuration < 500 && Math.abs(this.touchDeltaY) > Math.abs(this.touchDeltaX)) {
      this.close(win);
    }

    this.resetTouchState();
  }

  private resetTouchState() {
    this.touchStartY = null;
    this.touchStartX = null;
    this.touchStartTime = null;
    this.touchDeltaY = 0;
    this.touchDeltaX = 0;
    this.isClickingButton = false;
  }

  // Méthode spécifique pour les boutons en mode mobile
  onButtonTouchStart(event: TouchEvent) {
    if (!this.isMobile) return;
    
    // Marquer que nous sommes en train de cliquer sur un bouton
    this.isClickingButton = true;
    
    // Empêcher la propagation pour éviter les conflits avec les autres gestionnaires tactiles
    event.stopPropagation();
  }

  onButtonTouchEnd(event: TouchEvent) {
    if (!this.isMobile) return;
    
    // Empêcher la propagation pour éviter les conflits avec les autres gestionnaires tactiles
    event.stopPropagation();
    
    // Réinitialiser l'état après un court délai pour permettre au clic de se traiter
    setTimeout(() => {
      this.isClickingButton = false;
    }, 100);
  }
}
